import * as React from "react";
import { useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Download,
  Pen,
  Settings,
  Loader2,
  Search,
  X,
  UserCircle,
  LogIn,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  certificationsApi,
  studentApi,
  type Certification,
  CREDENTIAL_ISSUE_OVERRIDE_REASON_MIN_LEN,
  type IssueCredentialPayload,
} from "@/lib/api";
import config from "@/config";
import { usePermissions } from "@/hooks/usePermissions";
import { can } from "@/utils/permissions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatAdminAuditUtc, formatAdminBusinessEt } from "@/lib/admin-datetime";
import { ProcessCertificationModal } from "@/components/certifications/ProcessCertificationModal";
import { UserDetailsSheet } from "@/components/users/UserDetailsSheet";
import { EditUserDialog } from "@/components/users/EditUserDialog";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { useDataTable } from "@/hooks/use-data-table";

type UpdatableField =
  | "theory"
  | "clinical"
  | "payment"
  | "documentation"
  | "graduation";

const STATUS_OPTIONS: Record<UpdatableField, readonly string[]> = {
  theory: ["COMPLETED", "PENDING"],
  clinical: ["COMPLETED", "PENDING", "NOT_REQUIRED"],
  payment: ["COMPLETED", "PENDING"],
  documentation: ["APPROVED", "PENDING"],
  graduation: ["APPROVED", "PENDING"],
} as const;

const statusKeyToBoolean = (field: UpdatableField, key: string): boolean => {
  if (field === "theory" || field === "payment") {
    return key === "COMPLETED";
  }
  if (field === "clinical") {
    return key === "COMPLETED" || key === "NOT_REQUIRED";
  }
  return key === "APPROVED";
};

export default function Certifications() {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = React.useState<Certification[]>([]);
  const [pageCount, setPageCount] = React.useState<number>(-1);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [loading, setLoading] = React.useState(false);
  const [updating, setUpdating] = React.useState<Set<string>>(new Set());
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  const [detailsError, setDetailsError] = React.useState<string | null>(null);
  const [details, setDetails] = React.useState<Certification | null>(null);
  const [certificateNumberDialogOpen, setCertificateNumberDialogOpen] =
    React.useState(false);
  const [selectedCertificationForEdit, setSelectedCertificationForEdit] =
    React.useState<Certification | null>(null);
  const [certificateNumberValue, setCertificateNumberValue] =
    React.useState("");
  const [processCertificationModalOpen, setProcessCertificationModalOpen] =
    React.useState(false);
  const [selectedCertificationForProcess, setSelectedCertificationForProcess] =
    React.useState<Certification | null>(null);
  const { permissions, adminType } = usePermissions();
  const canImpersonate = can(permissions, "users.main.impersonate", adminType);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [sheetUserId, setSheetUserId] = React.useState<string | null>(null);
  const [sheetFallbackName, setSheetFallbackName] = React.useState<string>();
  const [sheetFallbackEmail, setSheetFallbackEmail] = React.useState<string>();
  const [sheetFallbackProgram, setSheetFallbackProgram] = React.useState<string>();
  const [editUserOpen, setEditUserOpen] = React.useState(false);
  const [impersonateConfirmOpen, setImpersonateConfirmOpen] =
    React.useState(false);
  const [impersonatingUserId, setImpersonatingUserId] = React.useState<
    string | null
  >(null);
  const [globalSearch, setGlobalSearch] = React.useState(
    () => searchParams.get("search") ?? ""
  );
  /** Keeps latest search for `buildParamsFromTableState` without changing that callback every keystroke (which would retrigger pagination fetch). */
  const globalSearchRef = React.useRef(globalSearch);
  globalSearchRef.current = globalSearch;
  const refetchRef = React.useRef<(() => void) | null>(null);
  const deepLinkOpenedRef = React.useRef(false);

  const normalizeCertification = React.useCallback(
    (item: any): Certification | null => {
      const studentId =
        item?.studentId != null ? String(item.studentId) : undefined;
      if (item && item.certification) {
        const c = item.certification;
        const enrolledProgram =
          item.enrolledProgram || item.program || undefined;
        return {
          ...c,
          studentId,
          // Keep stored programName (may be PENDING) so admin can override it.
          programName: c.programName,
          email: c.email || item.email,
          username:
            item.fullName ||
            c.fullName ||
            c.username ||
            item.username ||
            "",
          enrolledProgram,
          lastLogin: c.lastLogin || item.lastLogin,
          lastCertificateDownload:
            c.lastCertificateDownload || item.lastCertificateDownload,
        } as Certification;
      }
      if (
        item &&
        (item.email || item.username) &&
        (item.theory || item.payment || item.documentation)
      ) {
        return {
          ...(item as Certification),
          studentId,
          enrolledProgram: item.enrolledProgram || item.program,
        };
      }
      if (item && (item.email || item.username)) {
        const username = item.fullName || item.username || "";
        const pending = { key: "PENDING", value: false } as const;
        return {
          studentId,
          username,
          email: item.email,
          programName: item.certification?.programName || "PENDING",
          enrolledProgram: item.enrolledProgram || item.program,
          theory: { ...pending },
          payment: { ...pending },
          documentation: { key: "PENDING", value: false },
          graduation: { key: "PENDING", value: false },
          digitalBadge: { status: "PENDING" },
          letter: { status: "PENDING" },
          certificate: { status: "PENDING" },
          transcript: "",
          lastLogin: item.lastLogin,
          lastCertificateDownload: item.lastCertificateDownload,
        } as Certification;
      }
      return null;
    },
    []
  );

  const fetchCertifications = React.useCallback(
    async (params?: Record<string, string | number>) => {
      setLoading(true);
      try {
        const res = await certificationsApi.getAll(params);
        const raw = (res as any)?.data || [];
        const count = (res as any)?.count ?? 0;
        const perPage = (params?.perPage as number) || 10;
        
        const normalized = (
          Array.isArray(raw)
            ? raw.map(normalizeCertification).filter(Boolean)
            : []
        ) as Certification[];
        
        setRows(normalized);
        setTotalCount(count);
        setPageCount(count > 0 ? Math.ceil(count / perPage) : -1);
      } catch (e) {
        console.error("Failed to load certifications:", e);
        toast.error("Failed to load certifications");
      } finally {
        setLoading(false);
      }
    },
    [normalizeCertification]
  );

  const openDetails = React.useCallback(async (identifier: string) => {
    if (!identifier) {
      toast.error("Invalid certification: missing identifier");
      return;
    }
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError(null);
    setDetails(null);
    try {
      const res = await certificationsApi.getByIdentifier(identifier);
      setDetails(res.data);
    } catch (e) {
      setDetailsError(
        e instanceof Error ? e.message : "Failed to load details"
      );
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  // Deep-link from notifications: /certifications?search=email
  React.useEffect(() => {
    if (deepLinkOpenedRef.current || loading || !globalSearch.trim() || !rows.length) {
      return;
    }
    if (!searchParams.get("search")) return;
    const first = rows[0];
    const identifier = first?.email || first?.username;
    if (!identifier) return;
    deepLinkOpenedRef.current = true;
    void openDetails(identifier);
  }, [loading, rows, globalSearch, searchParams, openDetails]);

  const handleInlineUpdate = React.useCallback(async (
    row: Certification,
    field: UpdatableField,
    newKey: string
  ) => {
    const identifier = row.email || row.username;
    if (!identifier) {
      toast.error("Invalid certification: missing identifier");
      return;
    }
    const updateKey = `${identifier}-${field}`;
    const prevRows = [...rows];
    const nextRows = rows.map((r) => {
      if ((r.email || r.username) !== identifier) return r;
      const updated: any = { ...r };
      const valueBool = statusKeyToBoolean(field, newKey);
      if (field === "theory" || field === "clinical" || field === "payment") {
        updated[field] = { key: newKey, value: valueBool };
      } else if (field === "documentation" || field === "graduation") {
        const existing = (r as any)[field] || {};
        updated[field] = { ...existing, key: newKey, value: valueBool };
      }
      return updated as Certification;
    });
    setRows(nextRows);
    setUpdating((s) => new Set(s).add(updateKey));
    try {
      const valueBool = statusKeyToBoolean(field, newKey);
      await certificationsApi.updateByIdentifier(identifier, {
        [field]: { key: newKey as any, value: valueBool },
      });
    } catch (e) {
      setRows(prevRows);
      toast.error(e instanceof Error ? e.message : "Failed to update");
    } finally {
      setUpdating((s) => {
        const ns = new Set(s);
        ns.delete(updateKey);
        return ns;
      });
    }
  }, [rows]);

  const handleCertificateDownload = React.useCallback(async (certification: Certification) => {
    const identifier = certification.email || certification.username;
    if (!identifier) {
      toast.error("Invalid certification: missing identifier");
      return;
    }
    const updateKey = `${identifier}-download`;
    setUpdating((s) => new Set(s).add(updateKey));
    let url: string | null = null;
    try {
      const blob = await certificationsApi.downloadCertificate(identifier);
      url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const studentName = certification.username || "student";
      link.download = `certificate-${studentName.replace(/[^a-zA-Z0-9]/g, "-")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Certificate downloaded successfully");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to download certificate"
      );
    } finally {
      if (url) {
        window.URL.revokeObjectURL(url);
      }
      setUpdating((s) => {
        const ns = new Set(s);
        ns.delete(updateKey);
        return ns;
      });
    }
  }, []);

  const handleBadgeDownload = React.useCallback(async (certification: Certification) => {
    const identifier = certification.email || certification.username;
    if (!identifier) {
      toast.error("Invalid certification: missing identifier");
      return;
    }
    const updateKey = `${identifier}-badge-download`;
    setUpdating((s) => new Set(s).add(updateKey));
    let url: string | null = null;
    try {
      const blob = await certificationsApi.downloadBadge(identifier);
      url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const studentName = certification.username || "student";
      link.download = `badge-${studentName.replace(/[^a-zA-Z0-9]/g, "-")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Badge downloaded successfully");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to download badge");
    } finally {
      if (url) {
        window.URL.revokeObjectURL(url);
      }
      setUpdating((s) => {
        const ns = new Set(s);
        ns.delete(updateKey);
        return ns;
      });
    }
  }, []);

  const handleLetterDownload = React.useCallback(async (certification: Certification) => {
    const identifier = certification.email || certification.username;
    if (!identifier) {
      toast.error("Invalid certification: missing identifier");
      return;
    }
    const updateKey = `${identifier}-letter-download`;
    setUpdating((s) => new Set(s).add(updateKey));
    let url: string | null = null;
    try {
      const blob = await certificationsApi.downloadLetter(identifier);
      url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const studentName = certification.username || "student";
      link.download = `letter-${studentName.replace(/[^a-zA-Z0-9]/g, "-")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Letter downloaded successfully");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to download letter");
    } finally {
      if (url) {
        window.URL.revokeObjectURL(url);
      }
      setUpdating((s) => {
        const ns = new Set(s);
        ns.delete(updateKey);
        return ns;
      });
    }
  }, []);

  const handleCredentialStatusChange = React.useCallback(async (
    certification: Certification,
    credentialType: "certificate" | "badge" | "letter",
    newStatus: "PENDING" | "APPROVED" | "REJECTED" | "ISSUED"
  ) => {
    const identifier = certification.email || certification.username;
    if (!identifier) {
      toast.error("Invalid certification: missing identifier");
      return;
    }
    const updateKey = `${identifier}-${credentialType}`;
    setUpdating((s) => new Set(s).add(updateKey));
    try {
      if (newStatus === "ISSUED") {
        const issueOnce = async (payload?: IssueCredentialPayload) => {
          if (credentialType === "certificate") {
            await certificationsApi.issueCertificate(identifier, payload);
          } else if (credentialType === "badge") {
            await certificationsApi.issueBadge(identifier, payload);
          } else {
            await certificationsApi.issueLetter(identifier, payload);
          }
        };

        try {
          await issueOnce();
        } catch (e) {
          if (
            (e as { profileIncomplete?: boolean }).profileIncomplete === true
          ) {
            const reason = window.prompt(
              `Student portal profile is incomplete.\n\nEnter an audit override reason (minimum ${CREDENTIAL_ISSUE_OVERRIDE_REASON_MIN_LEN} characters), or Cancel to abort:`
            );
            if (!reason || reason.trim().length < CREDENTIAL_ISSUE_OVERRIDE_REASON_MIN_LEN) {
              throw e;
            }
            await issueOnce({
              overrideProfileIncomplete: true,
              overrideReason: reason.trim(),
            });
          } else {
            throw e;
          }
        }
        toast.success(
          `${credentialType.charAt(0).toUpperCase() + credentialType.slice(1)} issued successfully`
        );
      } else {
        if (credentialType === "certificate") {
          await certificationsApi.approveByIdentifier(identifier, {
            status: newStatus,
          });
        } else if (credentialType === "badge") {
          await certificationsApi.approveBadgeByIdentifier(identifier, {
            status: newStatus,
          });
        } else if (credentialType === "letter") {
          await certificationsApi.approveLetterByIdentifier(identifier, {
            status: newStatus,
          });
        }
        toast.success(
          `${credentialType.charAt(0).toUpperCase() + credentialType.slice(1)} status updated to ${newStatus}`
        );
      }
      if (refetchRef.current) {
        refetchRef.current();
      }
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : `Failed to update ${credentialType} status`
      );
    } finally {
      setUpdating((s) => {
        const ns = new Set(s);
        ns.delete(updateKey);
        return ns;
      });
    }
  }, []);

  const handleCertificateNumberSave = React.useCallback(async () => {
    if (!selectedCertificationForEdit) return;
    const identifier =
      selectedCertificationForEdit.email ||
      selectedCertificationForEdit.username;
    if (!identifier) {
      toast.error("Invalid certification: missing identifier");
      return;
    }
    const trimmedValue = certificateNumberValue.trim();
    if (!trimmedValue) {
      toast.error("Certificate number cannot be empty");
      return;
    }
    const updateKey = `${identifier}-certificateNumber`;
    setUpdating((s) => new Set(s).add(updateKey));
    try {
      await certificationsApi.updateByIdentifier(identifier, {
        certificateNumber: trimmedValue,
      });
      if (refetchRef.current) {
        refetchRef.current();
      }
      setCertificateNumberDialogOpen(false);
      setCertificateNumberValue("");
      toast.success("Certificate number updated successfully");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to update certificate number"
      );
    } finally {
      setUpdating((s) => {
        const ns = new Set(s);
        ns.delete(updateKey);
        return ns;
      });
    }
  }, [selectedCertificationForEdit, certificateNumberValue]);

  const openStudentSheet = React.useCallback((row: Certification) => {
    const studentId = row.studentId;
    if (!studentId) {
      toast.error("This row has no student account to open.");
      return;
    }
    setSheetUserId(studentId);
    setSheetFallbackName(row.username || row.email || "Student");
    setSheetFallbackEmail(row.email);
    setSheetFallbackProgram(row.programName);
    setSheetOpen(true);
  }, []);

  const openImpersonateConfirm = React.useCallback(() => {
    if (!sheetUserId) return;
    if (!config.studentAppUrl) {
      toast.error("Student portal URL is not configured (VITE_STUDENT_APP_URL).");
      return;
    }
    setImpersonateConfirmOpen(true);
  }, [sheetUserId]);

  const confirmViewAsUser = React.useCallback(async () => {
    if (!sheetUserId) return;

    setImpersonatingUserId(sheetUserId);
    try {
      const res = await studentApi.impersonate(sheetUserId);
      const baseUrl = res?.data?.impersonationUrl;
      if (!baseUrl) {
        throw new Error("No impersonation URL returned");
      }
      const returnTo = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      const url = new URL(baseUrl);
      url.searchParams.set("returnTo", returnTo);
      url.searchParams.set("sessionMode", "tab");
      window.open(url.toString(), "_blank", "noopener,noreferrer");
      toast.success(
        `Opened student portal as ${sheetFallbackName || "student"}`
      );
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Failed to start impersonation";
      toast.error(message);
    } finally {
      setImpersonatingUserId(null);
      setImpersonateConfirmOpen(false);
    }
  }, [sheetUserId, sheetFallbackName]);

  const columns = React.useMemo<ColumnDef<Certification>[]>(
    () => [
      {
        id: "username",
        accessorKey: "username",
        meta: { label: "Student Name" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Student Name" />
        ),
        cell: ({ row }) => (
          <button
            type="button"
            className="text-primary underline underline-offset-2"
            onClick={() => openDetails(row.original.email || row.original.username)}
            aria-label={`View details for ${row.original.username}`}
          >
            {row.original.username}
          </button>
        ),
      },
      {
        id: "email",
        accessorKey: "email",
        meta: { label: "Email" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Email" />
        ),
      },
      {
        id: "startDate",
        accessorKey: "startDate",
        meta: { label: "Start Date" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Start Date" />
        ),
        cell: ({ row }) =>
          row.original.startDate
            ? new Date(row.original.startDate).toLocaleDateString()
            : "-",
      },
      {
        id: "programName",
        accessorKey: "programName",
        meta: { label: "Program" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Program" />
        ),
        cell: ({ row }) => {
          const stored = row.original.programName?.trim() || "";
          const isPlaceholder =
            !stored || stored.toUpperCase() === "PENDING";
          const enrolled = row.original.enrolledProgram?.trim() || "";
          const display = isPlaceholder ? enrolled || stored || "-" : stored;
          const showEnrolledHint =
            !isPlaceholder &&
            Boolean(enrolled) &&
            enrolled.toLowerCase() !== stored.toLowerCase();
          return (
            <div className="flex flex-col">
              <span className="font-medium">{display}</span>
              {showEnrolledHint ? (
                <span className="text-xs text-muted-foreground">
                  Enrolled: {enrolled}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "mode",
        accessorKey: "mode",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Mode" />
        ),
        enableColumnFilter: true,
        meta: {
          label: "Mode",
          variant: "select",
          options: [
            { label: "Online", value: "online" },
            { label: "Hybrid", value: "hybrid" },
            { label: "On-campus", value: "on-campus" },
          ],
        },
      },
      {
        id: "theory",
        accessorFn: (row) => row.theory?.key,
        meta: { label: "Coursework" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Coursework" />
        ),
        cell: ({ row }) => (
          <Select
            value={row.original.theory?.key || ""}
            onValueChange={(v) => handleInlineUpdate(row.original, "theory", v)}
            disabled={updating.has(
              `${row.original.email || row.original.username}-theory`
            )}
          >
            <SelectTrigger>
              <SelectValue placeholder="-" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.theory.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      },
      {
        id: "clinical",
        accessorFn: (row) => row.clinical?.key || "NOT_REQUIRED",
        meta: { label: "Clinicals" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Clinicals" />
        ),
        cell: ({ row }) => (
          <Select
            value={row.original.clinical?.key || "NOT_REQUIRED"}
            onValueChange={(v) =>
              handleInlineUpdate(row.original, "clinical", v)
            }
            disabled={updating.has(
              `${row.original.email || row.original.username}-clinical`
            )}
          >
            <SelectTrigger>
              <SelectValue placeholder="-" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.clinical.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt.replace(/_/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      },
      {
        id: "payment",
        accessorFn: (row) => row.payment?.key,
        meta: { label: "Payments" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Payments" />
        ),
        cell: ({ row }) => (
          <Select
            value={row.original.payment?.key || ""}
            onValueChange={(v) =>
              handleInlineUpdate(row.original, "payment", v)
            }
            disabled={updating.has(
              `${row.original.email || row.original.username}-payment`
            )}
          >
            <SelectTrigger>
              <SelectValue placeholder="-" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.payment.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      },
      {
        id: "documentation",
        accessorFn: (row) => row.documentation?.key,
        meta: { label: "Documentation" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Documentation" />
        ),
        cell: ({ row }) => (
          <Select
            value={row.original.documentation?.key || ""}
            onValueChange={(v) =>
              handleInlineUpdate(row.original, "documentation", v)
            }
            disabled={updating.has(
              `${row.original.email || row.original.username}-documentation`
            )}
          >
            <SelectTrigger>
              <SelectValue placeholder="-" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.documentation.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      },
      {
        id: "graduation",
        accessorFn: (row) => row.graduation?.key,
        meta: { label: "Graduation" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Graduation" />
        ),
        cell: ({ row }) => (
          <Select
            value={row.original.graduation?.key || ""}
            onValueChange={(v) =>
              handleInlineUpdate(row.original, "graduation", v)
            }
            disabled={updating.has(
              `${row.original.email || row.original.username}-graduation`
            )}
          >
            <SelectTrigger>
              <SelectValue placeholder="-" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.graduation.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      },
      {
        id: "digitalBadge",
        accessorFn: (row) => row.digitalBadge?.status || "PENDING",
        meta: { label: "Digital Badge" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Digital Badge" />
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Select
              value={row.original.digitalBadge?.status || "PENDING"}
              onValueChange={(v) => {
                handleCredentialStatusChange(
                  row.original,
                  "badge",
                  v as any
                );
              }}
              disabled={updating.has(
                `${row.original.email || row.original.username}-badge`
              )}
            >
              <SelectTrigger>
                <SelectValue placeholder="-" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">PENDING</SelectItem>
                <SelectItem value="REJECTED">REJECTED</SelectItem>
                <SelectItem value="ISSUED">ISSUED</SelectItem>
              </SelectContent>
            </Select>
            {row.original.digitalBadge?.status === "ISSUED" && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => handleBadgeDownload(row.original)}
                disabled={updating.has(
                  `${row.original.email || row.original.username}-badge-download`
                )}
                title="Download Badge"
              >
                <Download className="h-4 w-4" />
              </Button>
            )}
          </div>
        ),
      },
      {
        id: "letter",
        accessorFn: (row) => row.letter?.status || "PENDING",
        meta: { label: "Letter" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Letter" />
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Select
              value={row.original.letter?.status || "PENDING"}
              onValueChange={(v) => {
                handleCredentialStatusChange(
                  row.original,
                  "letter",
                  v as any
                );
              }}
              disabled={updating.has(
                `${row.original.email || row.original.username}-letter`
              )}
            >
              <SelectTrigger>
                <SelectValue placeholder="-" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">PENDING</SelectItem>
                <SelectItem value="REJECTED">REJECTED</SelectItem>
                <SelectItem value="ISSUED">ISSUED</SelectItem>
              </SelectContent>
            </Select>
            {row.original.letter?.status === "ISSUED" && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => handleLetterDownload(row.original)}
                disabled={updating.has(
                  `${row.original.email || row.original.username}-letter-download`
                )}
                title="Download Letter"
              >
                <Download className="h-4 w-4" />
              </Button>
            )}
          </div>
        ),
      },
      {
        id: "certificate",
        accessorFn: (row) => row.certificate?.status || "PENDING",
        meta: { label: "Certificate" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Certificate" />
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Select
              value={row.original.certificate?.status || "PENDING"}
              onValueChange={(v) => {
                handleCredentialStatusChange(
                  row.original,
                  "certificate",
                  v as any
                );
              }}
              disabled={updating.has(
                `${row.original.email || row.original.username}-certificate`
              )}
            >
              <SelectTrigger>
                <SelectValue placeholder="-" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">PENDING</SelectItem>
                <SelectItem value="REJECTED">REJECTED</SelectItem>
                <SelectItem value="ISSUED">ISSUED</SelectItem>
              </SelectContent>
            </Select>
            {row.original.certificate?.status === "ISSUED" && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => handleCertificateDownload(row.original)}
                disabled={updating.has(
                  `${row.original.email || row.original.username}-download`
                )}
                title="Download Certificate"
              >
                <Download className="h-4 w-4" />
              </Button>
            )}
          </div>
        ),
      },
      {
        id: "transcript",
        accessorKey: "transcript",
        meta: { label: "Transcript" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Transcript" />
        ),
        cell: ({ row }) =>
          typeof row.original.transcript === "string"
            ? row.original.transcript
            : (row.original.transcript as any)?.url || "-",
      },
      {
        id: "certificateNumber",
        accessorKey: "certificateNumber",
        meta: { label: "Certificate Number" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Certificate Number" />
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="text-sm">
              {row.original.certificateNumber || "-"}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => {
                setSelectedCertificationForEdit(row.original);
                setCertificateNumberValue(
                  row.original.certificateNumber || ""
                );
                setCertificateNumberDialogOpen(true);
              }}
              aria-label="Edit certificate number"
            >
              <Pen className="h-3 w-3" />
            </Button>
          </div>
        ),
      },
      {
        id: "lastLogin",
        accessorKey: "lastLogin",
        meta: { label: "Last Login" },
        header: ({ column }) => (
          <div className="flex items-center gap-2">
            <LogIn className="h-4 w-4" />
            <DataTableColumnHeader column={column} label="Last Login" />
          </div>
        ),
        cell: ({ row }) =>
          row.original.lastLogin ? (
            <div className="text-sm">
              <div className="flex items-center gap-2">
                <LogIn className="h-3 w-3 text-green-500" />
                <div className="font-medium">
                  {new Date(row.original.lastLogin).toLocaleDateString()}
                </div>
              </div>
              <div className="text-muted-foreground text-xs ml-5">
                {new Date(row.original.lastLogin).toLocaleTimeString()}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <LogIn className="h-3 w-3" />
              <span>Never</span>
            </div>
          ),
      },
      {
        id: "lastCertificateDownload",
        accessorKey: "lastCertificateDownload",
        meta: { label: "Last Download" },
        header: ({ column }) => (
          <div className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            <DataTableColumnHeader column={column} label="Last Download" />
          </div>
        ),
        cell: ({ row }) =>
          row.original.lastCertificateDownload ? (
            <div className="text-sm">
              <div className="flex items-center gap-2">
                <Download className="h-3 w-3 text-blue-500" />
                <div className="font-medium">
                  {new Date(
                    row.original.lastCertificateDownload
                  ).toLocaleDateString()}
                </div>
              </div>
              <div className="text-muted-foreground text-xs ml-5">
                {new Date(
                  row.original.lastCertificateDownload
                ).toLocaleTimeString()}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Download className="h-3 w-3" />
              <span>Never</span>
            </div>
          ),
      },
      {
        id: "actions",
        meta: { label: "Actions" },
        enableHiding: false,
        header: () => <span className="sr-only">Actions</span>,
        // Width must match the buttons. A larger size pins Student Name too far
        // right and lets the email column show through the gap.
        size: 220,
        minSize: 220,
        maxSize: 220,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedCertificationForProcess(row.original);
                setProcessCertificationModalOpen(true);
              }}
            >
              <Settings className="h-4 w-4 mr-2" />
              Process
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!row.original.studentId}
              aria-label={`View student details for ${row.original.username || row.original.email}`}
              onClick={() => openStudentSheet(row.original)}
            >
              <UserCircle className="h-4 w-4 mr-2" />
              View
            </Button>
          </div>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [
      updating,
      handleInlineUpdate,
      handleCredentialStatusChange,
      handleCertificateDownload,
      handleBadgeDownload,
      handleLetterDownload,
      openDetails,
      openStudentSheet,
    ]
  );

  const { table } = useDataTable({
    data: rows,
    columns,
    pageCount: pageCount,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
    },
    getRowId: (row) => row.email || row.username || "",
  });

  const buildParamsFromTableState = React.useCallback(() => {
    const state = table.getState();
    const { pagination, sorting, columnFilters } = state;

    const params: Record<string, string | number> = {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
    };

    if (sorting.length > 0) {
      const sortString = sorting
        .map((s) => `${s.id}:${s.desc ? "desc" : "asc"}`)
        .join(",");
      params.sort = sortString;
    }

    columnFilters.forEach((filter) => {
      if (filter.value) {
        const value = Array.isArray(filter.value)
          ? filter.value.join(",")
          : String(filter.value);
        if (value) {
          params[filter.id] = value;
        }
      }
    });

    // Use global search for name, email, and program (read from ref so this
    // callback stays stable and pagination/sort effects do not refetch on
    // every keystroke).
    const searchTrim = globalSearchRef.current.trim();
    if (searchTrim) {
      params.search = searchTrim;
    }

    return params;
  }, [table]);

  const tableState = table.getState();
  const pageIndex = tableState.pagination.pageIndex;
  const pageSize = tableState.pagination.pageSize;
  const sortingKey = React.useMemo(() => JSON.stringify(tableState.sorting), [tableState.sorting]);
  const filtersKey = React.useMemo(() => JSON.stringify(tableState.columnFilters), [tableState.columnFilters]);

  // Debounced fetch when the search box changes only (not on every table churn).
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      const params = buildParamsFromTableState();
      if (globalSearch.trim()) {
        params.page = 1;
        table.setPageIndex(0);
      }
      void fetchCertifications(params);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [globalSearch, buildParamsFromTableState, fetchCertifications, table]);

  // Pagination, page size, sorting, and column filters — not tied to each search keystroke.
  React.useEffect(() => {
    void fetchCertifications(buildParamsFromTableState());
  }, [pageIndex, pageSize, sortingKey, filtersKey, fetchCertifications, buildParamsFromTableState]);

  const refetchCertifications = React.useCallback(() => {
    const params = buildParamsFromTableState();
    fetchCertifications(params);
  }, [buildParamsFromTableState, fetchCertifications]);

  React.useEffect(() => {
    refetchRef.current = refetchCertifications;
  }, [refetchCertifications]);

  return (
    <div className="space-y-4">
      {/* Stable header — search input here stays mounted, so typing is never
          re-rendered or blocked by table fetches. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>
            {totalCount > 0
              ? `${totalCount} certification${totalCount !== 1 ? "s" : ""}`
              : `${rows.length} certification${rows.length !== 1 ? "s" : ""}`}
          </span>
          {loading && (
            <span
              className="inline-flex items-center gap-1.5 text-xs"
              aria-live="polite"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Updating…
            </span>
          )}
        </div>

        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or program"
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            className="h-9 w-80 pl-8 sm:w-96"
          />
          {globalSearch && (
            <button
              onClick={() => setGlobalSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Table only — no overlay, no opacity. Previous rows stay visible
          while a new fetch is in flight; the small "Updating…" indicator
          in the header above signals progress. */}
      <DataTable table={table}>
        <DataTableToolbar table={table} />
      </DataTable>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Certification Details</DialogTitle>
          </DialogHeader>
          {detailsLoading ? (
            <div className="py-4 text-muted-foreground">Loading...</div>
          ) : detailsError ? (
            <div className="py-4 text-destructive">{detailsError}</div>
          ) : details ? (
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div>
                <strong>Student:</strong> {details.username} ({details.email})
              </div>
              <div>
                <strong>Program:</strong> {details.programName} • {details.mode}
              </div>
              <div>
                <strong>Start Date:</strong>{" "}
                {details.startDate
                  ? new Date(details.startDate).toLocaleDateString()
                  : "-"}
              </div>
              <div>
                <strong>Coursework:</strong> {details.theory?.key ?? "-"} |{" "}
                <strong>Clinical:</strong> {details.clinical?.key ?? "NOT_REQUIRED"} |{" "}
                <strong>Payment:</strong> {details.payment?.key ?? "-"}
              </div>
              <div>
                <strong>Documentation:</strong>{" "}
                {details.documentation?.key ?? "-"} |{" "}
                <strong>Graduation:</strong> {details.graduation?.key ?? "-"}
              </div>
              <div>
                <strong>Digital Badge:</strong>{" "}
                {details.digitalBadge?.status ?? "-"}{" "}
                {details.digitalBadge?.url
                  ? `• ${details.digitalBadge.url}`
                  : ""}
              </div>
              <div>
                <strong>Letter:</strong> {details.letter?.status ?? "-"}{" "}
                {details.letter?.url ? `• ${details.letter.url}` : ""}
              </div>
              <div>
                <strong>Transcript:</strong>{" "}
                {typeof details.transcript === "string"
                  ? details.transcript
                  : (details.transcript as any)?.url ? (
                    <a
                      className="text-primary underline"
                      href={
                        typeof details.transcript === "string"
                          ? details.transcript
                          : (details.transcript as any)?.url
                      }
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open
                    </a>
                  ) : (
                    "-"
                  )}
              </div>
              <div>
                <strong>Certificate:</strong>{" "}
                {details.certificate?.url ? (
                  <a
                    className="text-primary underline"
                    href={details.certificate.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Download
                  </a>
                ) : (
                  "-"
                )}
              </div>
              <div className="text-muted-foreground">
                <strong>Updated:</strong>{" "}
                {details.updatedAt ? formatAdminAuditUtc(details.updatedAt) : "-"}
              </div>
              {details.lastLogin && (
                <div className="text-muted-foreground">
                  <strong>Last Login:</strong> {formatAdminBusinessEt(details.lastLogin)}
                </div>
              )}
              {details.lastCertificateDownload && (
                <div className="text-muted-foreground">
                  <strong>Last Certificate Download:</strong>{" "}
                  {formatAdminBusinessEt(details.lastCertificateDownload)}
                </div>
              )}
            </div>
          ) : (
            <div className="py-4 text-muted-foreground">No details.</div>
          )}
          <DialogFooter>
            <Button type="button" onClick={() => setDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={certificateNumberDialogOpen}
        onOpenChange={setCertificateNumberDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Certificate Number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="certificateNumber">Certificate Number</Label>
              <Input
                id="certificateNumber"
                value={certificateNumberValue}
                onChange={(e) => setCertificateNumberValue(e.target.value)}
                placeholder="Enter certificate number"
                className="text-center"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCertificateNumberDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCertificateNumberSave}
              disabled={updating.has(
                `${
                  selectedCertificationForEdit?.email ||
                  selectedCertificationForEdit?.username
                }-certificateNumber`
              )}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProcessCertificationModal
        certification={selectedCertificationForProcess}
        open={processCertificationModalOpen}
        onOpenChange={setProcessCertificationModalOpen}
        onSuccess={refetchCertifications}
      />

      <UserDetailsSheet
        open={sheetOpen}
        userId={sheetUserId}
        fallbackName={sheetFallbackName}
        onOpenChange={setSheetOpen}
        onEdit={() => {
          if (!sheetUserId) {
            toast.error("This row has no student account to edit.");
            return;
          }
          setEditUserOpen(true);
        }}
        onDelete={() => toast.message("Delete is available from the Users page")}
        onResendCredentials={() =>
          toast.message("Resend credentials is available from the Users page")
        }
        onImpersonate={openImpersonateConfirm}
        canImpersonate={canImpersonate}
        resendingCredentials={false}
        impersonating={impersonatingUserId === sheetUserId}
      />

      <EditUserDialog
        open={editUserOpen}
        onOpenChange={setEditUserOpen}
        userId={sheetUserId}
        fallbackName={sheetFallbackName}
        fallbackEmail={sheetFallbackEmail}
        fallbackProgram={sheetFallbackProgram}
        onSaved={refetchCertifications}
      />

      <AlertDialog
        open={impersonateConfirmOpen}
        onOpenChange={(open) => !open && setImpersonateConfirmOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>View as user?</AlertDialogTitle>
            <AlertDialogDescription>
              This opens the student portal in a new tab signed in as{" "}
              <span className="font-medium">
                {sheetFallbackName || "this student"}
              </span>
              . Your admin session stays active in this tab. Actions are logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!impersonatingUserId}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void confirmViewAsUser();
              }}
              disabled={!sheetUserId || !!impersonatingUserId}
            >
              {impersonatingUserId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening...
                </>
              ) : (
                "Open student portal"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
